import {
  createContext,
  useContext,
  useState,
  PropsWithChildren,
  useCallback,
  useEffect,
} from "react";
import { useSocketContext } from "~contexts/Socket";
import { useToastNotificationContext } from "~contexts/ToastNotification";
import type { IPoll, IPollCreation } from "~contexts/Socket/interfaces";
import { IPollContext, DEFAULT_POLL_DATA } from "./interfaces";

const PollContext = createContext<IPollContext>(DEFAULT_POLL_DATA);

export const PollContextProvider = ({ children }: PropsWithChildren) => {
  const { socket } = useSocketContext();
  const { setToastData } = useToastNotificationContext();
  const [polls, setPolls] = useState<IPoll[]>([]);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const getPolls = useCallback(async () => {
    return new Promise((resolve, reject) => {
      try {
        socket?.emit("getSurveyData", (result: IPoll[]) => {
          resolve(result);
          setPolls(result);
        });
      } catch (error) {
        reject(error);
      }
    });
  }, [socket]);

  const onCreatePoll = (pollOptions: IPollCreation) => {
    const id = Date.now();
    const newOptions = [
      {
        id: `${id}-1`,
        text: pollOptions.option1,
        image: pollOptions.image1 ? pollOptions.image1 : null,
        porcentQuestion: 0,
      },
      {
        id: `${id}-2`,
        text: pollOptions.option2,
        image: pollOptions.image2 ? pollOptions.image2 : null,
        porcentQuestion: 0,
      },
    ];

    if (pollOptions.option3) {
      newOptions.push({
        id: `${id}-3`,
        text: pollOptions.option3,
        image: pollOptions.image3 ? pollOptions.image3 : null,
        porcentQuestion: 0,
      });
    }

    const newPoll = {
      finished: true,
      id,
      options: newOptions,
      question: pollOptions.question,
    };

    onRefreshPolls();
    setIsEditing(false);
    setToastData(
      { title: "informações salvas", text: "enquete cadastrada com sucesso" },
      "success"
    );
    socket?.emit("sendSurvey", newPoll);
  };

  const getOptionsPercentage = (updatedPoll: IPoll) => {
    const votes = updatedPoll.options.map((x) => x.count);
    const amountVotes = votes.reduce((acc, curr) => acc + curr);
    const percentage = votes.map((p) => Math.floor((p * 100) / amountVotes));

    updatedPoll.options.forEach((option, index) => {
      option.porcentQuestion = percentage[index];
    });

    return updatedPoll;
  };

  const onRefreshPolls = () => {
    setTimeout(() => {
      socket?.emit("getSurveyData", (result: IPoll[]) => {
        setPolls(result);
      });
    }, 100);
  };

  const onFinishPoll = (id: number) => {
    const pollToBeFinished = polls.filter((poll) => poll.id === id)[0];
    socket?.emit("updateSurvey", {
      ...getOptionsPercentage(pollToBeFinished),
      active: false,
    });

    onRefreshPolls();
  };

  const setPollActive = (id: number) => {
    const pollToBeActive = polls.filter((poll) => poll.id === id)[0];
    const isThereActivePoll = polls.some((poll) => poll.active);
    const activePollTitle = polls.find((p) => p.active)?.question;

    if (isThereActivePoll) {
      setToastData(
        {
          text: `você já tem uma enquete ativa, finalize a @${activePollTitle} para ativar outra`,
          title: "não podemos fazer isso",
        },
        "error"
      );
      return;
    }

    socket?.emit("updateSurvey", { ...pollToBeActive, active: true });
    onRefreshPolls();
  };

  useEffect(() => {
    getPolls();
  }, [getPolls]);

  return (
    <PollContext.Provider
      value={{
        polls,
        setPolls,
        isEditing,
        setIsEditing,
        onCreatePoll,
        onFinishPoll,
        onRefreshPolls,
        setPollActive,
      }}
    >
      {children}
    </PollContext.Provider>
  );
};

export const usePollContext = () => {
  const context = useContext(PollContext);

  return context;
};
